import React, { Children } from 'react';
import { motion } from 'framer-motion';
const ACCENT = '#2563EB';
const FONT = '"Poppins", "Inter", system-ui, sans-serif';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      ease: [0.2, 0.65, 0.3, 0.9]
    }
  }
};
const stagger = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08
    }
  }
};
type SectionProps = {
  title: string;
  children: React.ReactNode;
};
function Section({
  title,
  children
}: SectionProps) {
  return <motion.section variants={fadeUp} className="mb-12" data-id="element-2288" data-mp="1r8hjmkmonzzl">
      <h2 className="text-2xl md:text-3xl font-bold text-[#0F172A] mb-5 leading-[1.2]" data-id="element-2289" data-mp="1rb3rkldhkhic">
        {title}
      </h2>
      <div className="space-y-4 text-[15px] text-[#334155] leading-relaxed" data-id="element-2290" data-mp="32o685gfl572i">
        {children}
      </div>
    </motion.section>;
}
export function Privacy() {
  const lastUpdated = 'May 2026';
  return <div className="w-full min-h-screen bg-white text-[#1E293B]" style={{
    fontFamily: FONT
  }} data-id="element-2291" data-mp="1hlp8l8hgm6wa">
      {/* HERO */}
      <section className="pt-32 md:pt-36 pb-12 md:pb-16 px-6 md:px-10 border-b border-[#EDF1F7]" data-id="element-2292" data-mp="1dlezvo2a92dq">
        <div className="max-w-3xl mx-auto" data-id="element-2293" data-mp="18jplpzjrk8lf">
          <motion.div initial="hidden" animate="visible" variants={stagger} data-id="element-2294" data-mp="13lwk7jteo2rh">
            <motion.p variants={fadeUp} className="text-[11px] font-bold uppercase tracking-[0.3em] mb-6" style={{
            color: ACCENT
          }} data-id="element-2295" data-mp="13o0ldtzx6no2">
              Legal
            </motion.p>
            <motion.h1 variants={fadeUp} className="text-4xl md:text-5xl font-extrabold tracking-tight text-[#0F172A] leading-[1.05] mb-5" data-id="element-2296" data-mp="pvknf7lvlfu0">
              Privacy Policy
            </motion.h1>
            <motion.p variants={fadeUp} className="text-sm text-[#94A3B8]" data-id="element-2297" data-mp="fyg0h5k7hgc5">
              Last updated: {lastUpdated}
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* CONTENT */}
      <section className="py-16 md:py-20 px-6 md:px-10" data-id="element-2298" data-mp="1bzemxtwici0">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-3xl mx-auto" data-id="element-2299" data-mp="69is3viryl8w">
          <motion.p variants={fadeUp} className="text-[15px] text-[#334155] leading-relaxed mb-12" data-id="element-2300" data-mp="k04ehu7ftdbj">
            Structured Nutrition ("we," "us," or "our") respects your privacy.
            This Privacy Policy explains what information we collect, how we use
            it, and the choices you have. By using our website and services, you
            agree to the practices described below.
          </motion.p>

          <Section title="Information We Collect" data-id="element-2301" data-mp="12y6z7wsyco3j">
            <p data-id="element-2302" data-mp="17pozein5ikng">We collect information you provide directly to us, including:</p>
            <ul className="list-disc list-outside pl-6 space-y-2" data-id="element-2303" data-mp="17rt9xjfk2hwp">
              <li data-id="element-2304" data-mp="2set0enewbuyj">
                <strong data-id="element-2305" data-mp="2seau0xyis0cw">Contact details:</strong> Name, email address, and phone
                number when you submit our contact form or sign up for an
                account.
              </li>
              <li data-id="element-2306" data-mp="3255hu6rez80g">
                <strong data-id="element-2307" data-mp="327rqk9q1jtk9">Account information:</strong> Login credentials and
                profile data managed through our authentication provider
                (Firebase).
              </li>
              <li data-id="element-2308" data-mp="29j2y0z31wwk4">
                <strong data-id="element-2309" data-mp="24iew3idaavb4">Payment information:</strong> Processed securely through
                Stripe. We do not store full credit card numbers on our servers.
              </li>
              <li data-id="element-2310" data-mp="2jd30et6y7np5">
                <strong data-id="element-2311" data-mp="2egmabtg6uz9f">Communications:</strong> Messages you send us through
                forms, email, or social channels.
              </li>
              <li data-id="element-2312" data-mp="3ut1e5ad2mxwp">
                <strong data-id="element-2313" data-mp="3uwp8ucu95qd0">Usage data:</strong> Pages visited, time on site, and
                device/browser information collected through cookies and
                analytics tools.
              </li>
            </ul>
          </Section>

          <Section title="How We Use Your Information" data-id="element-2314" data-mp="24d7m7o4nrzgd">
            <p data-id="element-2315" data-mp="24dr1ex2xc9qp">We use the information we collect to:</p>
            <ul className="list-disc list-outside pl-6 space-y-2" data-id="element-2316" data-mp="28et1th8kuru9">
              <li data-id="element-2317" data-mp="2de5nvzrmjuvs">Provide and improve our coaching and digital products</li>
              <li data-id="element-2318" data-mp="2dc2owq8hurex">Process payments and deliver purchases</li>
              <li data-id="element-2319" data-mp="2ic784o0se08u">Respond to inquiries and provide customer support</li>
              <li data-id="element-2320" data-mp="2nc3e2st1o1r1">
                Send updates, newsletters, or marketing (only if you opt in)
              </li>
              <li data-id="element-2321" data-mp="2s2tgbx66bohi">
                Comply with legal obligations and protect against fraud or abuse
              </li>
            </ul>
          </Section>

          <Section title="Third-Party Services" data-id="element-2322" data-mp="2w6rdw3fuor8g">
            <p data-id="element-2323" data-mp="314jaharfx1iz">
              We share information with trusted third-party providers who help
              us operate our business:
            </p>
            <ul className="list-disc list-outside pl-6 space-y-2" data-id="element-2324" data-mp="360qc4rcszqv4">
              <li data-id="element-2325" data-mp="3g7kqk3k692b8">
                <strong data-id="element-2326" data-mp="3g82px924rwnd">Stripe</strong> — payment processing
              </li>
              <li data-id="element-2327" data-mp="3b6wv95owtzb7">
                <strong data-id="element-2328" data-mp="3b6di02a7fz3i">Firebase (Google)</strong> — authentication and database
              </li>
              <li data-id="element-2329" data-mp="368vcymzb640s">
                <strong data-id="element-2330" data-mp="36aeuegwpal45">Formspree</strong> — contact form submissions
              </li>
              <li data-id="element-2331" data-mp="32936qgxe09yl">
                <strong data-id="element-2332" data-mp="2rvb43w02plh">Google Analytics / Google Tag Manager</strong> — site
                analytics
              </li>
              <li data-id="element-2333" data-mp="3txwh3y13yt09">
                <strong data-id="element-2334" data-mp="3tzhbnnnfaby4">Meta Pixel</strong> — advertising and analytics
              </li>
            </ul>
            <p data-id="element-2335" data-mp="3p64yqas3vtl7">
              These providers have their own privacy policies governing how they
              handle your data.
            </p>
          </Section>

          <Section title="Cookies and Tracking" data-id="element-2336" data-mp="liwb8v8uwr6m">
            <p data-id="element-2337" data-mp="ljnwpd8qp8j6">
              We use cookies and similar technologies to remember your
              preferences, keep you logged in, analyze site traffic, and measure
              advertising effectiveness. You can disable cookies in your browser
              settings, though some site features may stop working as a result.
            </p>
          </Section>

          <Section title="Your Rights" data-id="element-2338" data-mp="218ng1onubi2g">
            <p data-id="element-2339" data-mp="217m4zk6tqvkq">Depending on where you live, you may have the right to:</p>
            <ul className="list-disc list-outside pl-6 space-y-2" data-id="element-2340" data-mp="267gt5fz7t723">
              <li data-id="element-2341" data-mp="1s8h5obnfuy7i">Access the personal information we hold about you</li>
              <li data-id="element-2342" data-mp="1sbd19tbtdass">Request that we correct or delete your information</li>
              <li data-id="element-2343" data-mp="1x9emokwppm7f">Opt out of marketing communications at any time</li>
              <li data-id="element-2344" data-mp="1ibvz3lvrtf1k">
                Withdraw consent for processing where consent is the legal basis
              </li>
            </ul>
            <p data-id="element-2345" data-mp="1nc06mmepazfn">
              To exercise any of these rights, contact us using the details
              below.
            </p>
          </Section>

          <Section title="Data Security" data-id="element-2346" data-mp="3ho6uakos78ou">
            <p data-id="element-2347" data-mp="3hn5gw9kama3y">
              We use reasonable administrative, technical, and physical
              safeguards to protect your information. However, no method of
              transmission over the internet is 100% secure, and we cannot
              guarantee absolute security.
            </p>
          </Section>

          <Section title="Children's Privacy" data-id="element-2348" data-mp="1fs7reattk2sx">
            <p data-id="element-2349" data-mp="1ayn0f2qn8938">
              Our services are not directed to individuals under the age of 18.
              We do not knowingly collect personal information from children. If
              you believe a child has provided us information, please contact us
              so we can delete it.
            </p>
          </Section>

          <Section title="Changes to This Policy" data-id="element-2350" data-mp="1yohc3gz87rlw">
            <p data-id="element-2351" data-mp="1tpnzrrvoe6nb">
              We may update this Privacy Policy from time to time. When we do,
              we'll revise the "Last updated" date at the top of this page.
              Significant changes will be communicated via the site or email.
            </p>
          </Section>

          <Section title="Contact Us" data-id="element-2352" data-mp="39omxk14tpxle">
            <p data-id="element-2353" data-mp="39nllbtc2n523">
              Questions about this Privacy Policy or your data? Reach out
              through our{' '}
              <a href="/contact" className="underline hover:no-underline" style={{
              color: ACCENT
            }} data-id="element-2354" data-mp="3eln6cjt25eg7">
                contact page
              </a>
              .
            </p>
          </Section>

          <motion.p variants={fadeUp} className="text-xs text-[#94A3B8] italic mt-16 pt-8 border-t border-[#EDF1F7]" data-id="element-2355" data-mp="2qrbzukyubc82">
            This document is provided as a general template and does not
            constitute legal advice. We recommend reviewing it with an attorney
            before relying on it.
          </motion.p>
        </motion.div>
      </section>
    </div>;
}